import { Suspense, useEffect } from 'react';
import { Outlet, useLocation, useNavigationType } from 'react-router-dom';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { PageSpinner } from '@/components/ui/Spinner';
import { cn } from '@/lib/utils';
import { useUIStore } from '@/stores/uiStore';
import { FeedbackProvider } from '@/components/providers/FeedbackProvider';
import { Sidebar } from './Sidebar';
import { Header } from './Header';
import { CommandPalette } from './CommandPalette';
import { FeatureNotification } from './FeatureNotification';

/**
 * The frame every signed-in page sits in: a dark chrome (sidebar and top bar,
 * one surface) with the page inset in it as a light panel behind a rounded
 * corner — the arrangement Engage uses.
 *
 * THE PAGE STILL SCROLLS THE WINDOW. The chrome is `position: fixed` and the
 * content flows beneath it, rather than the content living in its own
 * scrolling box. That is deliberate: printing, the browser's own scroll
 * restoration, and anything that reads `window.scrollY` all keep working
 * exactly as before. The rounded corner is therefore a small fixed piece drawn
 * over the content, not a clip on a scroll container.
 *
 * This component decides only what the frame looks like. Whether anyone may
 * see it is AppLayout's job.
 */
export function AppShell() {
  const location = useLocation();
  const navigationType = useNavigationType();
  const sidebarCollapsed = useUIStore((s) => s.sidebarCollapsed);
  const toggleSidebar = useUIStore((s) => s.toggleSidebar);

  // Keyboard shortcut for toggling sidebar (Cmd/Ctrl + B)
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key === 'b') {
        event.preventDefault();
        toggleSidebar();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [toggleSidebar]);

  // A new page starts at its top. Going Back or Forward (POP) is left to the
  // browser, which restores where that page had been scrolled to.
  useEffect(() => {
    if (navigationType !== 'POP') window.scrollTo({ top: 0 });
  }, [location.pathname, navigationType]);

  return (
    <FeedbackProvider>
      <div className="min-h-screen bg-canvas">
        <a
          href="#main-content"
          className="sr-only focus:not-sr-only focus:fixed focus:left-3 focus:top-2 focus:z-[70] focus:rounded-lg focus:bg-white focus:px-3 focus:py-1.5 focus:text-sm focus:font-medium focus:text-gray-900 focus:shadow-elevated"
        >
          Skip to content
        </a>

        <Sidebar collapsed={sidebarCollapsed} onToggle={toggleSidebar} />
        <Header sidebarCollapsed={sidebarCollapsed} />

        {/* The content panel's rounded top-left corner: a quarter-circle of
            chrome painted over the corner where sidebar and top bar meet. */}
        <div
          aria-hidden="true"
          className={cn(
            'pointer-events-none fixed top-header z-20 hidden h-4 w-4 transition-[left] duration-200 ease-out lg:block print:hidden',
            sidebarCollapsed ? 'left-16' : 'left-60'
          )}
          style={{
            background:
              'radial-gradient(circle at 100% 100%, transparent 1rem, rgb(var(--chrome)) calc(1rem + 0.5px))',
          }}
        />

        {/* Main Content */}
        <main
          id="main-content"
          tabIndex={-1}
          className={cn(
            'min-h-screen pt-header transition-[padding] duration-200 ease-out focus:outline-none print:p-0',
            // Mobile: no left padding
            'pl-0',
            // Desktop: adjust for sidebar
            sidebarCollapsed ? 'lg:pl-16' : 'lg:pl-60'
          )}
        >
          {/* Pages fill the whole panel beside the sidebar. There is no
              max-width and no centring: on a wide monitor a capped column
              left a wide empty gutter either side of the content. Where a
              page needs more room than the panel has (a wide table), that
              table scrolls inside its own container (`overflow-x-auto`);
              the window itself never scrolls sideways. */}
          <div className="p-4 md:p-6">
            {/* Route-scoped: a page that throws shows a message inside the
                shell, instead of unmounting the router and blanking the app. */}
            <ErrorBoundary scope="This page">
              {/* Pages load on demand (src/lib/lazyPage.ts). This boundary
                  sits inside the frame so the sidebar and top bar stay put
                  while one downloads. Navigations run as transitions, so once
                  a page is showing, moving to a page not yet downloaded keeps
                  the current one on screen until the next is ready; the
                  spinner is for the first page of a visit. */}
              <Suspense fallback={<PageSpinner delayMs={150} />}>
                <Outlet />
              </Suspense>
            </ErrorBoundary>
          </div>
        </main>

        <CommandPalette />

        {/* Feature Notification (one-time per user per feature) */}
        <FeatureNotification />
      </div>
    </FeedbackProvider>
  );
}
